"use client"

import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { ChevronRight, Loader2, Package, Heart } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { StepProps } from "@/types/listing"
import { cn } from "@/lib/utils"

type CreationType = "listing" | "wants"

export function CreationTypeStep({
  data,
  updateDataAction,
  onNextAction,
  hideButton,
  onValidationChange,
  compact,
}: StepProps) {
  const t = useTranslations("creation");
  const tCard = useTranslations("cardCreation.typeStep");
  const [status, setStatus] = useState<"loading" | "eligible" | "ineligible">("loading")
  const [reason, setReason] = useState<string | null>(null)
  const [selected, setSelected] = useState<CreationType>("listing")

  useEffect(() => {
    let cancelled = false
    fetch("/api/creation/seller-status")
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data.eligible) {
          setStatus("eligible")
        } else {
          setStatus("ineligible")
          setReason(data.reason ?? "You are not eligible to sell.")
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("ineligible")
          setReason("Unable to verify seller status.")
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    onValidationChange?.(status === "eligible")
  }, [status, onValidationChange])

  const handleSelect = (type: CreationType) => {
    setSelected(type)
  }

  if (status === "loading") {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 py-12">
        <Loader2
          className="h-6 w-6 shrink-0 animate-spin text-muted-foreground/70"
          aria-hidden
        />
        <p className="text-sm text-foreground/60">
          {tCard("checkingSeller")}
        </p>
      </div>
    )
  }

  if (status === "ineligible") {
    return (
      <div className="space-y-4 py-4">
        <div className="rounded-xl p-4 bg-muted/50">
          <p className="text-sm text-foreground/80">{reason}</p>
          <a
            href="/settings"
            className="mt-3 inline-block text-sm font-medium underline underline-offset-2 text-foreground/90"
          >
            {tCard("goToSettings")}
          </a>
        </div>
      </div>
    )
  }

  const optionClass = (isSelected: boolean, disabled = false) =>
    cn(
      "flex items-center gap-4 rounded-xl p-4 text-left transition-colors",
      disabled && "cursor-not-allowed opacity-50",
      !disabled && "bg-foreground/6 hover:bg-foreground/10",
      disabled && "bg-foreground/4",
      isSelected && !disabled && "ring-2 ring-inset ring-foreground/90"
    )

  return (
    <div className="space-y-6 w-full min-w-0 max-w-full">
      <div className="grid gap-2 w-full min-w-0">
        <button
          type="button"
          onClick={() => handleSelect("listing")}
          className={optionClass(selected === "listing")}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sheet-handle/80">
            <Package className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-foreground">{tCard("listing")}</span>
            <p className="mt-0.5 text-xs text-foreground/50">
              {tCard("listingDesc")}
            </p>
          </div>
        </button>

        <button
          type="button"
          disabled
          className={optionClass(false, true)}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted/80">
            <Heart className="h-5 w-5 text-muted-foreground/70" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-muted-foreground">
              {t("wants")}
            </span>
            <p className="mt-0.5 text-xs text-muted-foreground/70">
              {t("wantsDesc")}
            </p>
          </div>
        </button>
      </div>

      {!hideButton && (
        <div className="flex justify-end">
          <Button
            onClick={onNextAction}
            disabled={selected === "wants"}
            className={cn(
              "w-full rounded-full group",
              compact ? "h-14 text-sm shadow-md" : "h-16 text-base shadow-lg"
            )}
            size={compact ? "default" : "lg"}
          >
            {t("continue")}
            <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      )}
    </div>
  )
}
